/**
 * OI Historical Backfill page -- "1-minute data clean-up" (admin).
 *
 * Old backfill jobs run at 1 minute wrote 1-minute rows into the 5-minute OI tables.
 * 1) Scan (read-only) builds a per-day plan. 2) Apply (typed confirmation) backs up every
 * row it deletes, then deletes day by day; days changed since the scan are skipped.
 * 3) The 5-minute re-fetch windows can be copied into the backfill form above.
 * Restoring a backup is a server-side script (scripts/cleanup_oi_backfill_interval_mix.py restore).
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import oiIntervalCleanupService, {
  ACTIVE_CLEANUP_STATUSES,
  CLEANUP_CONFIRM_TEXT,
  type ApplyResultDay,
  type ApplySummary,
  type CleanupRun,
  type ScanPlanDay,
  type ScanSummary,
  type Windows,
} from "@/services/oiIntervalCleanupService";
import { extractErrorMessage } from "@/utils/errors";

const SYMBOLS = ["NIFTY", "BANKNIFTY"] as const;
const VERDICTS = ["CLEAN", "MIXED_5MIN_ON_GRID", "MIXED_1MIN_ON_GRID", "ONLY_1MIN", "MIXED_UNCERTAIN"] as const;
const VERDICT_LABEL: Record<string, string> = {
  CLEAN: "Clean",
  MIXED_5MIN_ON_GRID: "1-min rows added (5-min grid OK)",
  MIXED_1MIN_ON_GRID: "5-min grid holds 1-min rows",
  ONLY_1MIN: "Only 1-min rows",
  MIXED_UNCERTAIN: "Origin uncertain (kept)",
};
const STATUS_COLOR: Record<string, "default" | "info" | "warning" | "success" | "error"> = {
  PENDING: "default", RUNNING: "info", CANCEL_REQUESTED: "warning", COMPLETED: "success", FAILED: "error", CANCELLED: "default",
};
const num = (v: number | undefined | null) => (v === null || v === undefined ? "—" : v.toLocaleString("en-IN"));
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—");

export interface OIIntervalCleanupSectionProps {
  /** Copies a 5-minute re-fetch window into the backfill form above. */
  onUseWindow?: (symbol: string, from: string, to: string) => void;
}

function WindowsList({ windows, onUse }: { windows: Windows | undefined; onUse?: OIIntervalCleanupSectionProps["onUseWindow"] }) {
  const entries = Object.entries(windows ?? {});
  if (entries.length === 0) return <Typography variant="body2" color="text.secondary">No 5-minute re-fetch needed.</Typography>;
  return (
    <Stack spacing={1} data-testid="cleanup-windows">
      {entries.map(([ix, ws]) => (
        <Box key={ix}>
          <Typography variant="subtitle2">{ix}: {ws.length} window(s)</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {ws.map(([a, b]) => (
              <Chip key={`${ix}-${a}`} size="small" variant="outlined" label={a === b ? a : `${a} → ${b}`}
                onClick={onUse ? () => onUse(ix, a, b) : undefined}
                title={onUse ? "Copy into the backfill form above" : undefined} />
            ))}
          </Stack>
        </Box>
      ))}
      {onUse && <Typography variant="caption" color="text.secondary">Click a window to copy it into the backfill form above (5-minute interval).</Typography>}
    </Stack>
  );
}

function ScanDetail({ run, onUse }: { run: CleanupRun; onUse?: OIIntervalCleanupSectionProps["onUseWindow"] }) {
  const s = run.summary as ScanSummary;
  const plan = (run.plan ?? []) as ScanPlanDay[];
  const [page, setPage] = useState(0);
  return (
    <Stack spacing={2} data-testid="cleanup-scan-detail">
      <Grid container spacing={2}>
        {[
          ["Mixed index-days", num(s.mixed_index_days)],
          ["Rows to delete (spot / chain)", `${num(s.rows_to_delete.spot)} / ${num(s.rows_to_delete.chain)}`],
          ["5-min rows kept on those days", `${num(s.rows_kept_on_mixed_days.spot)} / ${num(s.rows_kept_on_mixed_days.chain)}`],
          ["Uncertain rows kept", `${num(s.rows_uncertain_kept.spot)} / ${num(s.rows_uncertain_kept.chain)}`],
        ].map(([label, value]) => (
          <Grid item xs={6} md={3} key={label}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="subtitle1" fontWeight={600}>{value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
      {!s.include_chain && <Alert severity="info">Spot only: the option chain was not scanned, so applying this scan leaves option rows unchanged.</Alert>}
      <Box>
        <Typography variant="subtitle2" gutterBottom>Days by verdict, per month</Typography>
        <TableContainer sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader data-testid="cleanup-months">
            <TableHead>
              <TableRow>
                <TableCell>Index</TableCell><TableCell>Month</TableCell>
                {VERDICTS.map((v) => <TableCell key={v} align="right">{VERDICT_LABEL[v]}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(s.by_month).flatMap(([ix, months]) => Object.entries(months).map(([m, c]) => (
                <TableRow key={`${ix}-${m}`}>
                  <TableCell>{ix}</TableCell><TableCell>{m}</TableCell>
                  {VERDICTS.map((v) => <TableCell key={v} align="right">{c[v] || ""}</TableCell>)}
                </TableRow>
              )))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box>
        <Typography variant="subtitle2" gutterBottom>5-minute re-fetch windows after the clean-up</Typography>
        <WindowsList windows={s.refetch_windows} onUse={onUse} />
      </Box>
      <Box>
        <Typography variant="subtitle2" gutterBottom>Per day ({plan.length})</Typography>
        <TableContainer>
          <Table size="small" data-testid="cleanup-plan">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell>Index</TableCell><TableCell>Verdict</TableCell>
                <TableCell align="right">Spot delete / keep</TableCell><TableCell align="right">Chain delete / keep</TableCell>
                <TableCell>Re-fetch</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plan.slice(page * 25, page * 25 + 25).map((p) => (
                <TableRow key={`${p.index}-${p.date}`}>
                  <TableCell>{p.date}{p.trading_day === false ? " (holiday)" : ""}</TableCell>
                  <TableCell>{p.index}</TableCell>
                  <TableCell>{VERDICT_LABEL[p.verdict] ?? p.verdict}</TableCell>
                  <TableCell align="right">{num(p.spot.delete)} / {num(p.spot.keep)}</TableCell>
                  <TableCell align="right">{num(p.chain.delete)} / {num(p.chain.keep)}</TableCell>
                  <TableCell>{p.refetch_5min ? "Yes" : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination component="div" count={plan.length} page={page} rowsPerPage={25} rowsPerPageOptions={[25]}
            onPageChange={(_e, p) => setPage(p)} />
        </TableContainer>
      </Box>
    </Stack>
  );
}

function ApplyDetail({ run, onUse }: { run: CleanupRun; onUse?: OIIntervalCleanupSectionProps["onUseWindow"] }) {
  const s = (run.summary ?? {}) as Partial<ApplySummary>;
  const skipped = ((run.plan ?? []) as ApplyResultDay[]).filter((r) => r.status !== "DELETED");
  return (
    <Stack spacing={2} data-testid="cleanup-apply-detail">
      <Typography variant="body2">
        Days processed {num(s.days_processed)} · deleted {num(s.days_deleted)} · skipped (changed since scan) {num(s.days_skipped_changed)} ·
        rows deleted: spot {num(s.rows_deleted?.spot)}, chain {num(s.rows_deleted?.chain)}
      </Typography>
      {run.backup_dir && (
        <Alert severity="info" data-testid="cleanup-backup">
          Backup on the server: <code>{run.backup_dir}</code>
          {s.backup && Object.entries(s.backup).map(([t, f]) => (
            <div key={t}>{f.file}: {num(f.rows)} rows, {(f.bytes / 1e6).toFixed(1)} MB, sha256 {f.sha256.slice(0, 12)}…</div>
          ))}
          Restore with <code>scripts/cleanup_oi_backfill_interval_mix.py restore --backup-dir …</code> (dry run first).
        </Alert>
      )}
      {skipped.length > 0 && (
        <Alert severity="warning">
          {skipped.length} day(s) changed since the scan and were left untouched: {skipped.slice(0, 10).map((r) => `${r.index} ${r.date}`).join(", ")}
          {skipped.length > 10 ? " …" : ""}. Run a new scan to clean them.
        </Alert>
      )}
      <Box>
        <Typography variant="subtitle2" gutterBottom>Re-fetch these windows at 5 minutes</Typography>
        <WindowsList windows={s.refetch_windows} onUse={onUse} />
      </Box>
    </Stack>
  );
}

export default function OIIntervalCleanupSection({ onUseWindow }: OIIntervalCleanupSectionProps) {
  const [symbols, setSymbols] = useState<string[]>(["NIFTY", "BANKNIFTY"]);
  const [fromDate, setFromDate] = useState("2023-09-01");
  const [toDate, setToDate] = useState(new Date(Date.now() + 330 * 60_000).toISOString().slice(0, 10));
  const [includeChain, setIncludeChain] = useState(true);
  const [runs, setRuns] = useState<CleanupRun[]>([]);
  const [detail, setDetail] = useState<CleanupRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [applyFor, setApplyFor] = useState<CleanupRun | null>(null);
  const [typed, setTyped] = useState("");

  const load = useCallback(async () => {
    try {
      setRuns(await oiIntervalCleanupService.listRuns(20));
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const anyActive = runs.some((r) => ACTIVE_CLEANUP_STATUSES.includes(r.status));
  useEffect(() => {
    if (!anyActive) return undefined;
    const t = globalThis.setInterval(() => {
      void load();
      if (detail) void oiIntervalCleanupService.getRun(detail.id).then(setDetail).catch(() => undefined);
    }, 5000);
    return () => globalThis.clearInterval(t);
  }, [anyActive, load, detail]);

  const startScan = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const run = await oiIntervalCleanupService.startScan({ symbol_codes: symbols, from_date: fromDate, to_date: toDate, include_chain: includeChain });
      setNotice("Scan started (read-only). It can take a few minutes with the option chain.");
      setDetail(null);
      await load();
      if (run.status === "COMPLETED") setDetail(await oiIntervalCleanupService.getRun(run.id));
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const view = async (id: string) => {
    try {
      setDetail(await oiIntervalCleanupService.getRun(id));
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const confirmApply = async () => {
    if (!applyFor) return;
    setBusy(true);
    setError(null);
    try {
      const run = await oiIntervalCleanupService.apply(applyFor.id, typed);
      setNotice("Clean-up started: every deleted row is backed up first. Backfill jobs are blocked until it finishes.");
      setApplyFor(null);
      setTyped("");
      await load();
      setDetail(await oiIntervalCleanupService.getRun(run.id));
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id: string) => {
    try {
      await oiIntervalCleanupService.cancel(id);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const applied = new Set(runs.filter((r) => r.kind === "APPLY" && r.status === "COMPLETED").map((r) => r.scan_run_id));
  const applySummary = applyFor?.summary as ScanSummary | undefined;

  return (
    <Card sx={{ mb: 3 }} data-testid="interval-cleanup-section">
      <CardContent>
        <Typography variant="h6" gutterBottom>1-minute data clean-up</Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Earlier backfill jobs run at 1 minute wrote 1-minute rows into these 5-minute OI tables. Scan first (read-only);
          applying deletes only backfilled rows that are off the 5-minute grid or were written by a 1-minute job. Live rows and
          rows from 5-minute jobs are kept. Every deleted row is backed up on the server first. Run scans and clean-ups
          outside market hours: they run inside the backend alongside the live feed.
        </Alert>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            {SYMBOLS.map((s) => (
              <FormControlLabel key={s} label={s} control={
                <Checkbox checked={symbols.includes(s)} onChange={() => setSymbols((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]))} />
              } />
            ))}
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={fromDate}
              onChange={(e) => setFromDate(e.target.value)} inputProps={{ "data-testid": "cleanup-from" }} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={toDate}
              onChange={(e) => setToDate(e.target.value)} inputProps={{ "data-testid": "cleanup-to" }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel control={<Switch checked={includeChain} onChange={(e) => setIncludeChain(e.target.checked)} />}
              label="Include option chain" />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button variant="contained" onClick={() => void startScan()} disabled={busy || symbols.length === 0 || anyActive}
              data-testid="cleanup-scan">Scan (read-only)</Button>
          </Grid>
        </Grid>
        {busy && <LinearProgress sx={{ mt: 2 }} />}
        {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mt: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

        <TableContainer sx={{ mt: 2 }}>
          <Table size="small" data-testid="cleanup-runs">
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell><TableCell>Kind</TableCell><TableCell>Status</TableCell><TableCell>Range</TableCell>
                <TableCell>Progress</TableCell><TableCell>Result</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((r) => {
                const s = r.summary as (ScanSummary & ApplySummary) | null;
                const pct = r.progress_total ? Math.round((100 * r.progress_done) / r.progress_total) : 0;
                return (
                  <TableRow key={r.id}>
                    <TableCell>{when(r.created_at)}</TableCell>
                    <TableCell>{r.kind === "SCAN" ? "Scan" : "Clean-up"}</TableCell>
                    <TableCell><Chip size="small" label={r.status} color={STATUS_COLOR[r.status]} /></TableCell>
                    <TableCell>{r.symbol_codes.join(", ")} {r.from_date} → {r.to_date}{r.include_chain ? "" : " (spot)"}</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {r.kind === "APPLY" ? <><LinearProgress variant="determinate" value={pct} />{r.progress_done}/{r.progress_total}</> : "—"}
                    </TableCell>
                    <TableCell>
                      {r.error ? <Typography variant="caption" color="error">{r.error}</Typography>
                        : !s ? "—"
                          : r.kind === "SCAN" ? `${num(s.mixed_index_days)} mixed days · delete ${num(s.rows_to_delete?.spot)} spot / ${num(s.rows_to_delete?.chain)} chain`
                            : `deleted ${num(s.rows_deleted?.spot)} spot / ${num(s.rows_deleted?.chain)} chain · skipped ${num(s.days_skipped_changed)} day(s)`}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => void view(r.id)}>View</Button>
                        {r.kind === "SCAN" && r.status === "COMPLETED" && (s?.mixed_index_days ?? 0) > 0 && !applied.has(r.id) && (
                          <Button size="small" color="error" variant="outlined" disabled={anyActive}
                            onClick={() => { setApplyFor(r); setTyped(""); }} data-testid={`cleanup-apply-${r.id}`}>Clean up…</Button>
                        )}
                        {ACTIVE_CLEANUP_STATUSES.includes(r.status) && (
                          <Button size="small" onClick={() => void cancel(r.id)}>Cancel</Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {runs.length === 0 && (
                <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No clean-up runs yet.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {detail && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {detail.kind === "SCAN" ? "Scan" : "Clean-up"} {detail.from_date} → {detail.to_date} · {detail.status}
            </Typography>
            {detail.status !== "COMPLETED" && !detail.summary ? (
              <Typography variant="body2" color="text.secondary">{detail.error ?? "Still running…"}</Typography>
            ) : detail.kind === "SCAN" ? <ScanDetail run={detail} onUse={onUseWindow} /> : <ApplyDetail run={detail} onUse={onUseWindow} />}
          </Box>
        )}
      </CardContent>

      <Dialog open={applyFor !== null} onClose={() => setApplyFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete 1-minute rows</DialogTitle>
        <DialogContent>
          {applySummary && (
            <Typography variant="body2" paragraph>
              This deletes {num(applySummary.rows_to_delete.spot)} spot and {num(applySummary.rows_to_delete.chain)} option-chain rows on{" "}
              {num(applySummary.mixed_index_days)} index-days ({applyFor?.from_date} → {applyFor?.to_date}). Each row is written to a
              backup on the server before it is deleted. Days that changed since the scan are skipped. Backfill jobs are blocked while it runs.
            </Typography>
          )}
          <Typography variant="body2" gutterBottom>Type <strong>{CLEANUP_CONFIRM_TEXT}</strong> to confirm.</Typography>
          <TextField fullWidth size="small" autoFocus value={typed} onChange={(e) => setTyped(e.target.value)}
            inputProps={{ "data-testid": "cleanup-confirm" }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyFor(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={typed !== CLEANUP_CONFIRM_TEXT || busy}
            onClick={() => void confirmApply()} data-testid="cleanup-confirm-apply">Back up and delete</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
